import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Save, CalendarClock, Mail, Phone, User, StickyNote, Check, ArrowLeft, Sparkles, Loader2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import AdminSearch from '@/components/admin/AdminSearch';
import AdminTabs from '@/components/admin/AdminTabs';
import ConfirmDialog from '@/components/admin/ConfirmDialog';
import DatePicker from '@/components/ui/date-picker';

const STATUSES = ['Pending', 'Confirmed', 'Completed', 'Cancelled'];

const statusPill = (status) => {
  switch (status) {
    case 'Pending': return 'bg-[#F2B705]/15 text-[#F2B705] border-[#F2B705]/30';
    case 'Confirmed': return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
    case 'Completed': return 'bg-green-500/15 text-green-300 border-green-500/30';
    case 'Cancelled': return 'bg-red-500/15 text-red-300 border-red-500/30';
    default: return 'bg-white/10 text-white/70 border-white/10';
  }
};

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [services, setServices] = useState([]);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveForm, setApproveForm] = useState({ date: '', service_id: '', duration_min: 60, total_price: 0, notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get('/admin/bookings'),
        api.get('/admin/services'),
      ]);
      setItems(b.data || []);
      setServices((s.data || []).filter((x) => x.active !== false));
      window.dispatchEvent(new Event('pg-refresh-unread'));
    } catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((b) => {
      if (statusFilter !== 'All' && b.status !== statusFilter) return false;
      if (!term) return true;
      return (
        b.name?.toLowerCase().includes(term) ||
        b.email?.toLowerCase().includes(term) ||
        b.service_title?.toLowerCase().includes(term) ||
        b.notes?.toLowerCase().includes(term)
      );
    });
  }, [items, q, statusFilter]);

  const selected = filtered.find((b) => b.id === selectedId) || filtered[0] || null;
  useEffect(() => { setNotes(selected?.admin_notes || ''); }, [selected?.id]);

  const setStatus = async (status) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/bookings/${selected.id}`, { status });
      setItems((prev) => prev.map((b) => (b.id === data.id ? data : b)));
      window.dispatchEvent(new Event('pg-refresh-unread'));
      toast.success(`Marked ${status}.`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/admin/bookings/${selected.id}`, { admin_notes: notes });
      setItems((prev) => prev.map((b) => (b.id === data.id ? data : b)));
      toast.success('Notes saved.');
    } catch { toast.error('Failed to save notes'); }
  };

  const del = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/bookings/${confirmDelete}`);
      setItems((prev) => prev.filter((b) => b.id !== confirmDelete));
      setConfirmDelete(null);
      setSelectedId(null);
      window.dispatchEvent(new Event('pg-refresh-unread'));
      toast.success('Deleted.');
    } catch { toast.error('Failed to delete'); }
  };

  const openApprove = () => {
    if (!selected) return;
    // Try to match a service by id/title.
    const svc = services.find((s) => s.id === selected.service_id) || services.find((s) => s.title === selected.service_title) || null;
    setApproveForm({
      date: selected.preferred_date || '',
      service_id: svc?.id || selected.service_id || '',
      duration_min: svc?.duration_min || 60,
      total_price: svc?.default_price || 0,
      notes: selected.notes || '',
    });
    setApproveOpen(true);
  };

  const submitApprove = async () => {
    if (!selected) return;
    if (!approveForm.date) { toast.error('Please pick a date & time for the session.'); return; }
    setApproving(true);
    try {
      const { data } = await api.post(`/admin/bookings/${selected.id}/approve`, {
        date: approveForm.date,
        service_id: approveForm.service_id || null,
        duration_min: Number(approveForm.duration_min) || 60,
        total_price: Number(approveForm.total_price) || 0,
        notes: approveForm.notes,
      });
      setItems((prev) => prev.map((b) => (b.id === data.booking.id ? data.booking : b)));
      setApproveOpen(false);
      window.dispatchEvent(new Event('pg-refresh-unread'));
      toast.success(data.client_created ? 'Approved. Client created and session added to calendar.' : 'Approved. Session added to calendar.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    } finally {
      setApproving(false);
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><CalendarClock className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Bookings</h1>
          <p className="mt-1 text-sm text-white/60">Booking requests from your service cards. Manage status and internal notes.</p>
        </div>
        <AdminTabs
          className="-mx-4 sm:mx-0"
          options={['All', ...STATUSES].map((s) => ({
            id: s,
            label: s,
            testid: `admin-bookings-filter-${s.toLowerCase()}`,
            count: s === 'All' ? undefined : items.filter((b) => b.status === s).length,
          }))}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>

      <div className="mt-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search bookings..." />
      </div>

      <div className="mt-6 grid lg:grid-cols-12 gap-4">
        <div data-testid={ADMIN.bookingsTable} className={`lg:col-span-5 rounded-2xl border border-white/10 bg-[#141414] overflow-hidden max-h-[72vh] flex flex-col ${selectedId ? 'hidden lg:flex' : 'flex'}`}>
          {loading ? <div className="p-6 text-sm text-white/60">Loading...</div> :
            filtered.length === 0 ? (
              <div className="p-10 text-center text-white/60">
                <CalendarClock className="w-8 h-8 mx-auto mb-3 text-white/30" />No bookings match.
              </div>
            ) : (
              <ul className="overflow-y-auto divide-y divide-white/5">
                {filtered.map((b) => {
                  const active = selected?.id === b.id;
                  return (
                    <li key={b.id}>
                      <button data-testid={ADMIN.bookingRow(b.id)} onClick={() => setSelectedId(b.id)} className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors ${active ? 'bg-white/[0.05]' : ''}`}>
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-white truncate">{b.name}</span>
                          <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${statusPill(b.status)}`}>{b.status}</span>
                        </div>
                        <div className="mt-1 text-xs text-white/60 truncate">{b.service_title}</div>
                        <div className="mt-1 text-xs text-white/50 truncate">{b.email}{b.preferred_date ? ` · ${b.preferred_date}` : ''}</div>
                        <div className="mt-1 text-[11px] text-white/40">{new Date(b.created_at).toLocaleString()}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
        </div>

        <div data-testid={ADMIN.bookingDetail} className={`lg:col-span-7 rounded-2xl border border-white/10 bg-[#141414] min-h-[60vh] flex flex-col ${selectedId ? 'flex' : 'hidden lg:flex'}`}>
          {!selected ? (
            <div className="m-auto text-white/50 text-sm">Select a booking to view details.</div>
          ) : (
            <>
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-white/5 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <button onClick={() => setSelectedId(null)} data-testid="admin-booking-back" className="lg:hidden inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mb-2"><ArrowLeft className="w-3.5 h-3.5" /> Back to list</button>
                  <div className="text-xs uppercase tracking-widest text-[#C9A227] font-semibold">Booking request</div>
                  <h2 className="font-display text-xl sm:text-2xl mt-1 leading-tight">{selected.service_title}</h2>
                  <div className="mt-2 text-[11px] text-white/40">Received {new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded-full shrink-0 ${statusPill(selected.status)}`}>{selected.status}</span>
              </div>
              <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
                <Info icon={User} label="Name" value={selected.name} />
                <Info icon={Mail} label="Email" value={selected.email} link={`mailto:${selected.email}`} />
                {selected.phone && <Info icon={Phone} label="Phone" value={selected.phone} />}
                {selected.preferred_date && <Info icon={CalendarClock} label="Preferred date" value={selected.preferred_date} />}
                {selected.session_type && <Info icon={Check} label="Session type" value={selected.session_type} />}
                {selected.notes && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-white/50">Player notes</div>
                    <p className="mt-1 text-sm text-white/85 whitespace-pre-wrap">{selected.notes}</p>
                  </div>
                )}
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-2"><StickyNote className="w-3 h-3" /> Admin notes</div>
                  <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Private notes for this booking..." className="mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#F2B705] focus:ring-2 focus:ring-[#F2B705]/30 outline-none transition-colors resize-none" />
                  <div className="mt-2 flex justify-end">
                    <button onClick={saveNotes} data-testid="admin-booking-save-notes" className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/5 inline-flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Save notes</button>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-white/5 flex flex-wrap items-center gap-2">
                <select data-testid={ADMIN.bookingStatusSelect} value={selected.status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white focus:border-[#F2B705] focus:ring-2 focus:ring-[#F2B705]/30 outline-none transition-colors">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {selected.status !== 'Confirmed' && !selected.session_id && (
                  <button data-testid="admin-booking-approve" onClick={openApprove} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] text-sm font-semibold">
                    <Sparkles className="w-4 h-4" /> Approve & Book
                  </button>
                )}
                {selected.session_id && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                    <Check className="w-3.5 h-3.5" /> Session on calendar
                  </span>
                )}
                <div className="grow" />
                <button data-testid={ADMIN.bookingDelete} onClick={() => setConfirmDelete(selected.id)} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          open={!!confirmDelete}
          onOpenChange={(v) => !v && setConfirmDelete(null)}
          title="Delete this booking?"
          description="The booking request will be permanently removed. This cannot be undone."
          confirmText="Delete booking"
          destructive
          onConfirm={del}
        />
      )}

      {approveOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70" onClick={() => !approving && setApproveOpen(false)} />
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#141414] p-5 sm:p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--pg-admin-accent)] font-semibold">Approve booking</div>
                <h3 className="font-display text-2xl mt-1">{selected.name}</h3>
                <p className="text-[11px] text-white/50 mt-0.5">This will create a client (if new) and add the session to the calendar.</p>
              </div>
              <button onClick={() => !approving && setApproveOpen(false)} className="p-2 rounded-lg hover:bg-white/5" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 block">Session date & time</span>
                <DatePicker
                  value={approveForm.date}
                  onChange={(v) => setApproveForm((f) => ({ ...f, date: v }))}
                  showTime
                  minDate={new Date()}
                  placeholder="Pick a date and time"
                  testId="approve-date"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 block">Service / Session type</span>
                  <select
                    data-testid="approve-service"
                    value={approveForm.service_id}
                    onChange={(e) => {
                      const svc = services.find((s) => s.id === e.target.value);
                      setApproveForm((f) => ({
                        ...f,
                        service_id: e.target.value,
                        duration_min: svc?.duration_min || f.duration_min,
                        total_price: svc?.default_price ?? f.total_price,
                      }));
                    }}
                    className="w-full border border-white/10 bg-black/30 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[var(--pg-admin-accent)]"
                  >
                    <option value="">— Free-form —</option>
                    {services.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 block">Duration (min)</span>
                  <input type="number" min={15} max={600} value={approveForm.duration_min} onChange={(e) => setApproveForm((f) => ({ ...f, duration_min: e.target.value }))} className="w-full border border-white/10 bg-black/30 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[var(--pg-admin-accent)]" />
                </label>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 block">Total price (SAR)</span>
                <input type="number" step="0.01" min={0} value={approveForm.total_price} onChange={(e) => setApproveForm((f) => ({ ...f, total_price: e.target.value }))} className="w-full border border-white/10 bg-black/30 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[var(--pg-admin-accent)]" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1.5 block">Notes (optional)</span>
                <textarea rows={2} value={approveForm.notes} onChange={(e) => setApproveForm((f) => ({ ...f, notes: e.target.value }))} className="w-full border border-white/10 bg-black/30 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-[var(--pg-admin-accent)] resize-none" />
              </label>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setApproveOpen(false)} disabled={approving} className="px-3 py-2 rounded-xl border border-white/10 text-sm hover:bg-white/5">Cancel</button>
              <button data-testid="approve-submit" onClick={submitApprove} disabled={approving} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--pg-admin-accent)] hover:bg-[var(--pg-admin-accent-hover)] text-[var(--pg-admin-accent-fg)] text-sm font-semibold disabled:opacity-60">
                {approving ? <><Loader2 className="w-4 h-4 animate-spin" /> Approving…</> : <><Sparkles className="w-4 h-4" /> Approve & Book</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ icon: Icon, label, value, link }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-2"><Icon className="w-3 h-3" /> {label}</div>
    <div className="mt-0.5 text-sm">{link ? <a href={link} className="text-[#F2B705] hover:underline">{value}</a> : value}</div>
  </div>
);
